import type {
  IAuthStore,
  IQueryClient,
  IWorkspaceStore,
  TQueryClientFactory,
  WorkspaceData,
} from "@workspace/types";
import { makeAutoObservable, reaction } from "mobx";
import { injectable, inject } from "inversify";
import { ServiceID } from "./symbols";
import { getOrCreate } from "@workspace/api";

@injectable()
export class WorkspaceStore implements IWorkspaceStore {
  private workspacesQuery: IQueryClient<WorkspaceData[]>;

  constructor(
    @inject(ServiceID.QueryClientFactory)
    private queryClientFactory: TQueryClientFactory,
    @inject(ServiceID.AuthStore)
    private authStore: IAuthStore,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.workspacesQuery = this.queryClientFactory<WorkspaceData[]>(() => ({
      queryKey: ["workspaces", authStore.user?.id],
      queryFn: () =>
        getOrCreate({
          name: this.authStore.user?.email
            ? `${this.authStore.user.email} workspace`
            : "Default workspace",
        }),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000,
      enabled: !!(authStore.isAuthenticated && authStore.user?.id),
    }));
  }

  get workspaces(): WorkspaceData[] | undefined {
    return this.workspacesQuery.result.data;
  }

  get activeWorkspace(): WorkspaceData | undefined {
    return this.workspacesQuery.result.data?.[0];
  }

  get activeWorkspaceId(): WorkspaceData["workspace"]["id"] | undefined {
    return this.activeWorkspace?.workspace.id || undefined;
  }

  get isWorkspaceReady() {
    return !!(this.workspacesQuery.result.isSuccess && this.activeWorkspaceId);
  }

  get isLoading() {
    return this.workspacesQuery.result.isLoading;
  }

  dispose(): void {
    this.workspacesQuery.queryClient.clear();
  }
}
